/**
 * Advanced Cache Manager for Frontend Performance
 * Features:
 * - In-memory LRU cache for fast access
 * - LocalStorage persistence for heavy data across sessions
 * - Stale-while-revalidate pattern
 * - Request deduplication
 * - Prefetching support
 */

const STORAGE_PREFIX = 'ams_cache_';
const MAX_MEMORY_ITEMS = 100;
const MAX_STORAGE_SIZE = 5 * 1024 * 1024; // 5MB limit for localStorage

class CacheManager {
  constructor() {
    this.memoryCache = new Map();
    this.pendingRequests = new Map();
    this.accessOrder = [];
    this.storageEnabled = this.checkStorageAvailable();
    this.listeners = new Map();
  }

  /**
   * Check if localStorage is available
   */
  checkStorageAvailable() {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Generate cache key
   */
  generateKey(url, params = {}) {
    const companyId = localStorage.getItem('selectedCompanyId') || 'default';
    const paramStr = Object.keys(params).sort().map(k => `${k}=${JSON.stringify(params[k])}`).join('&');
    return `${companyId}:${url}?${paramStr}`;
  }

  /**
   * LRU eviction for memory cache
   */
  evictLRU() {
    while (this.memoryCache.size >= MAX_MEMORY_ITEMS && this.accessOrder.length > 0) {
      const oldestKey = this.accessOrder.shift();
      this.memoryCache.delete(oldestKey);
    }
  }

  /**
   * Update access order for LRU
   */
  updateAccessOrder(key) {
    const idx = this.accessOrder.indexOf(key);
    if (idx > -1) this.accessOrder.splice(idx, 1);
    this.accessOrder.push(key);
  }

  /**
   * Get from memory cache
   */
  getFromMemory(key) {
    const cached = this.memoryCache.get(key);
    if (cached) {
      this.updateAccessOrder(key);
      return cached;
    }
    return null;
  }

  /**
   * Get from localStorage (for persistent heavy data)
   */
  getFromStorage(key) {
    if (!this.storageEnabled) return null;
    try {
      const stored = localStorage.getItem(STORAGE_PREFIX + key);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.warn('Cache storage read error:', e);
    }
    return null;
  }

  /**
   * Save to localStorage
   */
  saveToStorage(key, data, ttl) {
    if (!this.storageEnabled) return;
    try {
      const entry = {
        data,
        expiry: Date.now() + ttl,
        timestamp: Date.now()
      };
      const serialized = JSON.stringify(entry);
      
      // Check size before storing
      if (serialized.length > MAX_STORAGE_SIZE / 10) {
        console.warn('Cache entry too large for storage:', key);
        return;
      }
      
      localStorage.setItem(STORAGE_PREFIX + key, serialized);
    } catch (e) {
      // Storage full - clear old entries
      this.cleanupStorage();
    }
  }

  /**
   * Cleanup old storage entries
   */
  cleanupStorage() {
    if (!this.storageEnabled) return;
    const now = Date.now();
    const keysToRemove = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) {
        try {
          const entry = JSON.parse(localStorage.getItem(key));
          if (entry.expiry < now) {
            keysToRemove.push(key);
          }
        } catch {
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(k => localStorage.removeItem(k));
  }

  /**
   * Get cached data with stale-while-revalidate support
   * @returns {object} { data, isStale, isFresh }
   */
  get(url, params = {}, options = {}) {
    const key = this.generateKey(url, params);
    const now = Date.now();
    
    // Check memory cache first
    let cached = this.getFromMemory(key);
    
    // Check storage if not in memory and persistence enabled
    if (!cached && options.persist) {
      cached = this.getFromStorage(key);
      if (cached) {
        // Restore to memory
        this.memoryCache.set(key, cached);
        this.updateAccessOrder(key);
      }
    }
    
    if (!cached) return null;
    
    const isFresh = now < cached.expiry;
    const isStale = !isFresh && options.staleTime && now < (cached.expiry + options.staleTime);
    
    if (isFresh || isStale) {
      return {
        data: cached.data,
        isStale,
        isFresh,
        age: now - cached.timestamp
      };
    }
    
    return null;
  }

  /**
   * Set cache entry
   */
  set(url, params = {}, data, ttl = 30000, options = {}) {
    const key = this.generateKey(url, params);
    
    this.evictLRU();
    
    const entry = {
      data,
      expiry: Date.now() + ttl,
      timestamp: Date.now()
    };
    
    this.memoryCache.set(key, entry);
    this.updateAccessOrder(key);
    
    // Persist heavy data to localStorage
    if (options.persist) {
      this.saveToStorage(key, data, ttl);
    }
    
    // Notify listeners
    this.notifyListeners(url, data);
  }

  /**
   * Request deduplication - prevents duplicate concurrent requests
   */
  async dedupedFetch(key, fetchFn) {
    // If there's already a pending request, return that promise
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }
    
    // Create new request promise
    const promise = fetchFn().finally(() => {
      this.pendingRequests.delete(key);
    });
    
    this.pendingRequests.set(key, promise);
    return promise;
  }

  /**
   * Invalidate cache entries matching pattern
   */
  invalidatePattern(pattern) {
    // Memory cache
    for (const key of this.memoryCache.keys()) {
      if (key.includes(pattern)) {
        this.memoryCache.delete(key);
      }
    }
    
    // Storage
    if (this.storageEnabled) {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key?.startsWith(STORAGE_PREFIX) && key.includes(pattern)) {
          localStorage.removeItem(key);
        }
      }
    }
  }

  /**
   * Clear all caches
   */
  clear() {
    this.memoryCache.clear();
    this.accessOrder = [];
    
    if (this.storageEnabled) {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key?.startsWith(STORAGE_PREFIX)) {
          localStorage.removeItem(key);
        }
      }
    }
  }

  /**
   * Subscribe to cache updates
   */
  subscribe(pattern, callback) {
    if (!this.listeners.has(pattern)) {
      this.listeners.set(pattern, new Set());
    }
    this.listeners.get(pattern).add(callback);
    
    return () => {
      this.listeners.get(pattern)?.delete(callback);
    };
  }

  /**
   * Notify listeners of cache updates
   */
  notifyListeners(url, data) {
    for (const [pattern, callbacks] of this.listeners) {
      if (url.includes(pattern)) {
        callbacks.forEach(cb => cb(data));
      }
    }
  }

  /**
   * Prefetch data in background
   */
  async prefetch(url, fetchFn, ttl = 60000, options = {}) {
    const key = this.generateKey(url, {});
    const cached = this.get(url, {}, options);
    
    // Don't prefetch if fresh data exists
    if (cached?.isFresh) return;
    
    try {
      const data = await this.dedupedFetch(key, fetchFn);
      if (data?.success !== false) {
        this.set(url, {}, data, ttl, options);
      }
    } catch (e) {
      console.warn('Prefetch failed:', url, e);
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    let storageSize = 0;
    let storageCount = 0;
    
    if (this.storageEnabled) {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(STORAGE_PREFIX)) {
          storageCount++;
          storageSize += localStorage.getItem(key)?.length || 0;
        }
      }
    }
    
    return {
      memoryItems: this.memoryCache.size,
      storageItems: storageCount,
      storageSize: `${(storageSize / 1024).toFixed(2)} KB`,
      pendingRequests: this.pendingRequests.size
    };
  }
}

// Singleton instance
export const cacheManager = new CacheManager();

// Cache configuration presets
export const CACHE_PRESETS = {
  // Dashboard data - short TTL, high priority
  DASHBOARD: {
    ttl: 20000,        // 20 seconds
    staleTime: 60000,  // 1 minute stale allowed
    persist: false
  },
  // Employee list - medium TTL, persist
  EMPLOYEES: {
    ttl: 60000,        // 1 minute
    staleTime: 300000, // 5 minutes stale
    persist: true
  },
  // Reports - longer TTL
  REPORTS: {
    ttl: 120000,       // 2 minutes
    staleTime: 600000, // 10 minutes stale
    persist: true
  },
  // Static data (shifts, departments)
  STATIC: {
    ttl: 300000,       // 5 minutes
    staleTime: 900000, // 15 minutes stale
    persist: true
  },
  // Real-time data (punch feed)
  REALTIME: {
    ttl: 10000,        // 10 seconds
    staleTime: 30000,  // 30 seconds stale
    persist: false
  }
};

export default cacheManager;
